import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { signOut } from '@/services/auth';

interface NavigationHeaderProps {
  userType?: 'admin' | 'vereador' | 'instituicao' | 'gestor' | null;
  userName?: string;
}

export function NavigationHeader({ userType, userName }: NavigationHeaderProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch { void 0; }
    localStorage.removeItem('userType');
    localStorage.removeItem('userName');
    toast({
      title: "Logout realizado",
      description: "Até logo!",
    });
    navigate('/login');
  };

  return (
    <header className="bg-card border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <Link
              to="/"
              className="inline-flex items-center justify-center h-12 w-12 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
              aria-label="Início"
            >
              <img src="/favicon.ico" alt="Sistema de Emendas" className="h-6 w-6 sm:h-7 sm:w-7" loading="lazy" />
            </Link>
            <div>
              <h1 className="text-base sm:text-xl font-bold text-foreground">Sistema de Emendas</h1>
              {/* <p className="text-[11px] sm:text-xs text-muted-foreground">
                {userType === 'admin' ? 'Painel Administrativo' : 'Portal de Emendas dos Vereadores'}
              </p> */}
            </div>
          </div>

          {/* Botão hamburguer no mobile */}
          <div className="flex items-center sm:hidden">
            {userType && (
              <div className="text-right mr-2">
                <p className="hidden text-sm font-medium text-foreground leading-tight" aria-hidden="true">
                  {userName || 'Usuário'}
                </p>
                <p className="hidden text-xs text-muted-foreground" aria-hidden="true">
                  {userType === 'admin' ? 'Administrador' : 'Vereador'}
                </p>
              </div>
            )}
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Abrir menu"
              aria-controls="primary-navigation"
              aria-expanded={menuOpen}
              className="inline-flex items-center justify-center h-12 w-12 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
            >
              {/* Ícone hamburguer (SVG leve) */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6" aria-hidden="true">
                <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
              </svg>
            </button>
          </div>

          <nav
            id="primary-navigation"
            aria-label="Navegação principal"
            className="hidden sm:flex items-center space-x-2 sm:space-x-4"
          >
            {/* Links de navegação baseados no tipo de usuário */}
            {userType === 'admin' ? (
              <>
                <Link to="/admin" className="text-muted-foreground hover:text-foreground px-4 py-3 rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background inline-flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 mr-2" aria-hidden="true"><path d="M12 2l7 3v5c0 5-3.8 9.7-7 11-3.2-1.3-7-6-7-11V5l7-3zm0 4.2L7 6.9v3.1c0 3.8 2.5 7.7 5 9.2 2.5-1.5 5-5.4 5-9.2V6.9l-5-1.7zm0 2.8a2.5 2.5 0 012.5 2.5c0 1.1-.7 2-1.7 2.3l.7 2.4h-3l.7-2.4A2.5 2.5 0 019.5 11 2.5 2.5 0 0112 9z" /></svg>
                  Admin do Sistema
                </Link>
                <Link to="/relatorio" className="text-muted-foreground hover:text-foreground px-4 py-3 rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background inline-flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 mr-2" aria-hidden="true"><path d="M6 2h9l5 5v13a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2zm9 2H6v16h12V8h-5V4z" /></svg>
                  Relatórios
                </Link>
              </>
            ) : (
              userType ? (
                null
              ) : (
                <Link to="/login" className="text-muted-foreground hover:text-foreground px-4 py-3 rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background inline-flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 mr-2" aria-hidden="true"><path d="M10 17l-1.4-1.4 3.6-3.6H3v-2h9.2l-3.6-3.6L10 5l6 6-6 6zm8 4H12v-2h6V5h-6V3h6a2 2 0 012 2v14a2 2 0 01-2 2z" /></svg>
                  Login
                </Link>
              )
            )}

            {/* Informações do usuário e logout */}
            {userType && (
              <div className="flex items-center space-x-3 ml-4 pl-4 border-l border-border">
                <div className="text-right">
                  <p className="hidden text-sm font-medium text-foreground" aria-hidden="true">
                    {userName || 'Usuário'}
                  </p>
                  <p className="hidden text-xs text-muted-foreground" aria-hidden="true">
                    {userType === 'admin' ? 'Administrador' : userType === 'vereador' ? 'Vereador' : userType === 'gestor' ? 'Gestor' : 'Instituição'}
                  </p>
                </div>
                <a
                  href="#logout"
                  onClick={(e) => { e.preventDefault(); handleLogout(); }}
                  className="text-muted-foreground hover:text-foreground px-4 py-3 rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background inline-flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 mr-2" aria-hidden="true"><path d="M10 17l-1.4-1.4 3.6-3.6H3v-2h9.2l-3.6-3.6L10 5l6 6-6 6zm8 4H12v-2h6V5h-6V3h6a2 2 0 012 2v14a2 2 0 01-2 2z" /></svg>
                  Sair
                </a>
              </div>
            )}
          </nav>
        </div>

        {/* Menu mobile colapsável */}
        <div className={`${menuOpen ? 'block' : 'hidden'} sm:hidden`}>
          <nav aria-label="Menu mobile" className="pt-2 pb-3 space-y-1">
            {userType === 'admin' ? (
              <>
                <Link to="/admin" className="block px-4 py-3 rounded-md text-base font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background">
                  Admin do Sistema
                </Link>
                <Link to="/relatorio" className="block px-4 py-3 rounded-md text-base font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background">
                  Relatórios
                </Link>
              </>
            ) : userType === 'vereador' ? (
              <></>
            ) : (
              <Link to="/login" className="block px-4 py-3 rounded-md text-base font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background">
                Login
              </Link>
            )}

            {userType && (
              <div className="mt-2 px-4">
                <a
                  href="#logout"
                  onClick={(e) => { e.preventDefault(); handleLogout(); }}
                  className="block px-4 py-3 rounded-md text-base font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background inline-flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 mr-2" aria-hidden="true"><path d="M10 17l-1.4-1.4 3.6-3.6H3v-2h9.2l-3.6-3.6L10 5l6 6-6 6zm8 4H12v-2h6V5h-6V3h6a2 2 0 012 2v14a2 2 0 01-2 2z" /></svg>
                  Sair
                </a>
              </div>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}
